import { BsChevronRight } from '@react-icons/all-files/bs/BsChevronRight';
import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline';
import useSWR from 'swr';

import { CloudinaryImage } from 'components/common/CloudinaryImage';
import { YourWatch } from 'components/common/summary/YourWatch';
import { Slideout } from 'components/design-system/Slideout';
import { CommissionDetailsModal } from 'components/offers/CommissionDetailsModal';
import { OrderStatus } from 'consts/order_statuses';
import { useModal } from 'context/modal/ModalContext';
import { clientApiGet } from 'lib/api_helper';
import { Order } from 'types';
import { formatPrice } from 'utils/text-helpers';

import { cancelledStatusStyle, statusLabels, statusStyle } from './constants';
import { OrderStatusLoaderContent, OrderStatusLoaderTitle } from './OrderStatusLoader';

import { ShippingInstructionsModal } from '../ShippingInstructionsModal';
import { StatusDetailsModal } from '../StatusDetailsModal';

type OrderStatusSlideoutProps = {
  orderFromProps?: Order;
  orderId?: number;
  closeModal: () => void;
};

export const OrderStatusSlideout: React.FC<OrderStatusSlideoutProps> = ({ orderFromProps, orderId, ...props }) => {
  const { openModal, closeModal } = useModal();
  const { data } = useSWR<Order>(orderId ? '/api/orders/' + orderId : null, clientApiGet);

  const order = orderFromProps || data;

  if (!order) {
    return (
      <Slideout {...props} title={<OrderStatusLoaderTitle />}>
        <OrderStatusLoaderContent />
      </Slideout>
    );
  }

  const commissionCents = order.salePriceCents - order.payoutAmountCents;

  const openCommissionModal = () => {
    openModal(
      <CommissionDetailsModal
        commission={commissionCents}
        minimalFee={0}
        sellingPrice={order.salePriceCents}
        earnings={order.payoutAmountCents}
        sellerFee={order.sellerFeeCents}
        closeModal={closeModal}
      />,
      true
    );
  };

  const onClickStatus = () => openModal(<StatusDetailsModal order={order} closeModal={closeModal} />, true);

  const onClickShipping = () =>
    openModal(
      <ShippingInstructionsModal labelUrl={order.receiptShipment?.shippingLabelUrl} closeModal={closeModal} />,
      true
    );

  if (!order.listing) {
    return null;
  }

  return (
    <Slideout
      {...props}
      title={
        <div className='text-center riforma-regular gap-2'>
          <div className='fs-18px'>Order details</div>
          <div className='fs-14px text-secondary'>Order #{order.id}</div>
        </div>
      }
    >
      <YourWatch listing={order.listing} className='fs-14px mb-4 border-bottom border-top py-3' />
      <div className='d-flex flex-column gap-4'>
        <div>
          <span className='riforma-regular fs-16px'>Status</span>
          <div
            className='rounded-2 p-4 mt-3'
            style={order.status === OrderStatus.CANCELLED ? cancelledStatusStyle : statusStyle}
            onClick={onClickStatus}
            role='button'
          >
            <div className='d-flex align-items-center justify-content-between'>
              <span className='riforma-medium fs-14px'>{statusLabels[order.status]}</span>
              <BsChevronRight />
            </div>
          </div>
        </div>
        {order.status === OrderStatus.AWAITING_RECEIPT && (
          <div>
            <div className='riforma-regular fs-16px'>Shipping</div>
            <div className='fs-12px text-secondary mt-2 riforma-regular'>
              You have <span className='text-decoration-underline'>24 hours</span> to ship the watch to us
            </div>
            <div className='border rounded-2 p-4 mt-3' onClick={onClickShipping} role='button'>
              <div className='d-flex align-items-center justify-content-between'>
                <span className='riforma-medium fs-14px'>Shipping instructions</span>
                <div className='d-flex align-items-center gap-2'>
                  <CloudinaryImage src='fedex-logo.png' alt='Fedex logo' width={60} height={20} />
                  <BsChevronRight />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className='riforma-regular'>
          <div className='fs-16px'>Receipt</div>
          <div className='d-flex w-100 justify-content-between mb-1 mt-3'>
            <div className=''>Selling price</div>
            <div>{formatPrice(order.salePriceCents)}</div>
          </div>
          <div className='d-flex w-100 justify-content-between mb-1 opacity-60'>
            <div role='button' onClick={openCommissionModal} className='d-flex align-items-center'>
              Bezel Commission <MdInfoOutline className='ms-1' size={16} />
            </div>
            <div>{formatPrice(commissionCents)}</div>
          </div>
          <div className='text-success d-flex justify-content-between fs-16px mb-4 riforma-medium'>
            <div>Payout</div>
            <div className='letter-spacing-005'>{formatPrice(order.payoutAmountCents)}</div>
          </div>
        </div>
      </div>
    </Slideout>
  );
};
